// Phone-only: keeps the page behind the mobile menu and the cart drawer from
// scrolling.
//
// `overflow: hidden` on <html> is not enough on iOS Safari: touches on a drawer
// that is shorter than the screen still scroll the page underneath. The
// reliable fix is to pin <body> with position: fixed while a drawer is open
// and give the scroll position back when it closes.
//
// What counts as "open" is read from the theme's own state, never set here:
// the menu <details> gets `.menu-open`, the cart <theme-drawer> gets `[open]`.
(() => {
  const phone = window.matchMedia('(max-width: 749px)');
  const root = document.documentElement;
  let locked = false;
  let scrollY = 0;

  // Only the mobile menu pins <body>. Pinning it for the cart too made iOS Safari change its toolbars
  // while the cart opened from a scrolled page, and the drawer then ran under the address bar until it
  // was closed and opened again. The cart leaves the page alone and just swallows the touches that
  // would scroll the page behind it (see below).
  const drawerIsOpen = () => phone.matches && Boolean(document.querySelector('.menu-drawer-container.menu-open'));
  const cartIsOpen = () => phone.matches && Boolean(document.querySelector('#cart-drawer[open]'));

  // Cart open: a vertical swipe may only scroll the drawer's own list, and only while that list has
  // room to move in that direction; anything else would drag the page underneath. Sideways swipes
  // (the upsell chips) are left alone.
  let touchStart = null;
  document.addEventListener(
    'touchstart',
    (event) => {
      touchStart = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    },
    { passive: true }
  );
  document.addEventListener(
    'touchmove',
    (event) => {
      if (!touchStart || event.touches.length !== 1 || !cartIsOpen()) return;
      const dx = event.touches[0].clientX - touchStart.x;
      const dy = event.touches[0].clientY - touchStart.y;
      if (Math.abs(dx) > Math.abs(dy)) return;
      const target = event.target instanceof Element ? event.target : null;
      const scroller = target?.closest('#cart-drawer .cart-drawer__inner');
      const canScroll =
        scroller &&
        scroller.scrollHeight > scroller.clientHeight &&
        (dy > 0 ? scroller.scrollTop > 0 : scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1);
      if (!canScroll && event.cancelable) event.preventDefault();
    },
    { passive: false }
  );

  // A sticky header that reappeared on scroll-up sits against the scrolling page. Once <body> is
  // pinned that scrolling is gone and the header would jump to its natural place above the
  // screen (a blank white screen with the menu panel gone). Hold it where it was on screen.
  const headerSection = () => document.querySelector('.header-section');

  // Pinning <body> makes the page jump to scroll position 0 and unpinning jumps it back.
  // The sticky header (assets/header.js) reads those jumps as the user scrolling to the top
  // and down again: it drops its "shown on scroll-up" state, and after the cart or menu
  // closes the header (logo, cart button) is left hidden above the screen. Hide these
  // scroll events from every listener; they arrive on the next frame or two.
  let muteScroll = 0;
  const muteScrollEvents = () => {
    muteScroll += 1;
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        setTimeout(() => {
          muteScroll -= 1;
        }, 60)
      )
    );
  };
  window.addEventListener(
    'scroll',
    (event) => {
      if (muteScroll > 0) event.stopImmediatePropagation();
    },
    { capture: true }
  );

  const lock = () => {
    if (locked) return;
    muteScrollEvents();
    scrollY = window.scrollY;
    const section = headerSection();
    const topBefore = section ? section.getBoundingClientRect().top : 0;
    document.body.style.top = `-${scrollY}px`;
    root.classList.add('trop-scroll-locked');
    locked = true;
    if (section) {
      const shift = topBefore - section.getBoundingClientRect().top;
      if (Math.abs(shift) > 0.5) {
        section.style.setProperty('position', 'relative', 'important');
        section.style.setProperty('top', `${shift}px`, 'important');
      }
    }
  };

  const unlock = () => {
    if (!locked) return;
    muteScrollEvents();
    const section = headerSection();
    if (section) {
      section.style.removeProperty('position');
      section.style.removeProperty('top');
    }
    root.classList.remove('trop-scroll-locked');
    document.body.style.top = '';
    window.scrollTo({ top: scrollY, behavior: 'instant' });
    locked = false;
  };

  // The mobile menu opens below the header (announcement bar included): tell the CSS where that is.
  const publishMenuTop = () => {
    const header = document.querySelector('header, .header');
    if (!header) return;
    // Never negative: a header scrolled off the top must not push the menu off screen.
    root.style.setProperty('--trop-menu-top', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
  };

  const update = () => {
    if (drawerIsOpen()) {
      if (!locked) {
        publishMenuTop();
        // The header can change height in the next frame (menu icon swapped for the X), so measure once more.
        requestAnimationFrame(() => {
          const header = document.querySelector('header, .header');
          if (drawerIsOpen() && header && header.getBoundingClientRect().bottom > 0) publishMenuTop();
        });
      }
      lock();
    } else {
      unlock();
    }
  };

  new MutationObserver(update).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'open'],
  });
  phone.addEventListener('change', update);
  // The menu top was measured when it opened. Re-measuring while the page is pinned is
  // only safe if the header is still in view (browser toolbars fire resize while scrolling).
  window.addEventListener('resize', () => {
    const header = document.querySelector('header, .header');
    if (drawerIsOpen() && header && header.getBoundingClientRect().bottom > 0) publishMenuTop();
  });
  update();
})();
